import { QueryClient } from '@tanstack/react-query';
import { WordlistsApi } from 'src/api';
import { WordlistsWithProgressResponse, WordlistWithProgress } from 'src/api/wordlists/types';

export const isWordlistComplete = (wordlist: WordlistWithProgress): boolean =>
  wordlist.isCompleted ||
  (wordlist.learnablesCount > 0 && wordlist.learnedLearnablesCount >= wordlist.learnablesCount);

export const isWordlistLearnable = (wordlist: WordlistWithProgress): boolean =>
  wordlist.learnablesCount > 0 && !isWordlistComplete(wordlist);

/**
 * The next word list a vocab-track learner should move on to.
 *
 * The track the learner picked is stored as an ordered collection, so "carry on"
 * means the first list after the active one that still has something to learn.
 * Lists the placement test already completed are skipped rather than treated as
 * the end of the track. Returns null when the active list is outside the
 * collection (e.g. a community list), because there is no track order to follow.
 */
export const findNextTrackWordlistId = ({
  wordlists,
  collectionWordlistIds,
  activeWordlistId,
}: WordlistsWithProgressResponse): string | null => {
  if (!activeWordlistId) return null;

  const activeIndex = collectionWordlistIds.indexOf(activeWordlistId);
  if (activeIndex === -1) return null;

  const wordlistsById = new Map(wordlists.map(wordlist => [wordlist.wordlistId, wordlist]));

  for (const wordlistId of collectionWordlistIds.slice(activeIndex + 1)) {
    const wordlist = wordlistsById.get(wordlistId);
    if (wordlist && isWordlistLearnable(wordlist)) {
      return wordlistId;
    }
  }

  return null;
};

export const isActiveTrackWordlistComplete = ({
  wordlists,
  activeWordlistId,
}: WordlistsWithProgressResponse): boolean => {
  const activeWordlist = wordlists.find(wordlist => wordlist.wordlistId === activeWordlistId);
  return !!activeWordlist && isWordlistComplete(activeWordlist);
};

export type AdvanceTrackWordlistResult = 'advanced' | 'track-complete' | 'not-needed';

/**
 * Activates the next word list in the learner's track so Learn has something to
 * teach. Progress is re-fetched rather than read from cache, so callers can act on
 * the outcome: continue into Learn when it advanced or was never needed, and offer
 * the track picker only once the whole track is finished.
 */
export const advanceToNextTrackWordlist = async (
  languagePairId: number,
  queryClient: QueryClient,
): Promise<AdvanceTrackWordlistResult> => {
  const wordlistsData = await queryClient.fetchQuery({
    queryKey: ['getWordlistsWithProgress', languagePairId],
    queryFn: () => WordlistsApi.getWordlistsWithProgress(languagePairId),
    staleTime: 0,
  });

  if (!isActiveTrackWordlistComplete(wordlistsData)) return 'not-needed';

  const nextWordlistId = findNextTrackWordlistId(wordlistsData);
  if (!nextWordlistId) return 'track-complete';

  await WordlistsApi.activateWordlist(languagePairId, nextWordlistId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['getWordlistsWithProgress', languagePairId] }),
    queryClient.invalidateQueries({ queryKey: ['getWordlist', languagePairId] }),
  ]);

  return 'advanced';
};
